/**
 * Skins that exist. Three are obtainable from the Closed Alpha banner
 * (ADR 0047); the first one ever shipped is historical and disabled
 * (ADR 0037, retired by ADR 0039).
 *
 * ## Why a file of its own
 *
 * The same reason artifacts and pets have theirs: `definitions.ts` assembles the
 * catalogue and must not also be where content is authored. This is the content.
 *
 * ## What a skin is, and what it is not
 *
 * It is a cosmetic worn in one loadout slot (ADR 0036). It has no level, is
 * never merged, grants **no attribute of any kind**, and cannot reach damage,
 * health, speed, cadence, range, hitbox or any economy. A skin changes what a
 * ship looks like and nothing else.
 *
 * It is UNIVERSAL: one definition works with every hull. There is deliberately
 * no definition per ship - the ship catalogue is expected to grow, and a skin
 * locked to one model dies the day its owner flies another.
 *
 * ## The free claim is retired (ADR 0039)
 *
 * ADR 0037 shipped this skin with a free, once-per-account claim. That is no
 * longer the product: **skins are obtained only by rolling the Cash gacha.**
 * There is no `redeemable` flag here any more, no route that grants one and no
 * service method that could. The flag was removed rather than set to `false`,
 * because a field named "redeemable" is a promise about a mechanism, and
 * keeping the field would leave the mechanism one edit away from returning.
 *
 * ## Why the definition stays, and stays DISABLED
 *
 * The id is permanent: `inventory_events` records it forever, and an
 * append-only history that no longer parses is not a history. Any account that
 * claimed one while the claim existed still owns it, can still see it, and can
 * still take it off.
 *
 * `enabled: false` is what stops it coming back. It is the flag the vault
 * already checks before granting anything, so retirement needs no new
 * mechanism - `grantInstanceCore` refuses `definition_disabled` for this id the
 * same way it refuses it for a retired pet.
 *
 * It is disabled because it is INCOMPLETE, not because it was withdrawn: there
 * is no PNG, no `assetKey`, no VFX descriptor and no Cash banner it could
 * belong to. It becomes obtainable when those exist and somebody decides a
 * price - not before, and not by accident.
 *
 * ## Sockets and jewels are a different gate
 *
 * A skin instance carries a socket CAPACITY from ADR 0040 onwards, drawn once
 * when the instance is created. Nothing in this file knows about it: capacity
 * lives on the instance, never on the definition, because two copies of the
 * same skin legitimately have different capacities. Jewels do not exist.
 */

/** Permanent and immutable. Owned rows reference it forever. */
export const FOUNDATION_SKIN_ID = 'skin_foundation_violet_pulse';

/**
 * The provenance the retired free claim wrote. **Legacy only.**
 *
 * Kept so historical rows remain readable and remain BOUND: an instance granted
 * while the claim existed carries this string, `SOULBOUND_ORIGINS` contains it,
 * and removing it would silently turn somebody's old free cosmetic into a
 * tradable item. That is the one thing retirement must not do.
 *
 * **Nothing produces it any more.** There is no route, no service method and no
 * migration that writes this origin, and `packages/shared` has no export that
 * would help one - it is referenced by the soulbound policy and by the tests
 * that prove it is unreachable, and nowhere else.
 */
export const FOUNDATION_SKIN_LEGACY_ORIGIN = 'foundation_grant';

export interface SkinDefinitionEntry {
  readonly id: string;
  /**
   * A chave que encontra a arte no manifesto de skins.
   *
   * Uma IDENTIDADE, não geometria: o pivô, o hash, os limites visuais e a
   * configuração de partículas continuam vivendo só em
   * `assets-source/skins/<id>/skin.json`. Esta string é o ponto de encontro
   * entre o catálogo de regras e o manifesto, e `skinReadiness` recusa quando
   * os dois discordam.
   *
   * `null` para conteúdo sem arte - hoje só a Foundation, que é histórica.
   */
  readonly assetKey: string | null;
  /** Shown to the player, in the language the game speaks. */
  readonly displayName: string;
  /** One sentence saying what it is. Never a promise about what it does. */
  readonly blurb: string;
  /**
   * Whether this definition may be granted at all.
   *
   * The vault's own gate, not a second one: `enabled: false` is what
   * `grantInstanceCore` already refuses on. A skin with no art and no banner is
   * disabled here, and no code path anywhere can produce a copy of it.
   */
  readonly enabled: boolean;
}

export const SKIN_DEFINITIONS: readonly SkinDefinitionEntry[] = Object.freeze([
  /*
   * As três do primeiro banner do Alpha Fechado (ADR 0047).
   *
   * Só PALAVRAS e identidade. O que uma skin PARECE - pivô, escala, partículas,
   * selo, LOD - está no descritor que o pipeline valida, e chega aqui pelo
   * `assetKey` e por mais nada.
   *
   * As três dividem a mesma raridade de inventário e a mesma celebração. Não há
   * hierarquia entre elas, e inventar uma seria decidir uma coisa que ninguém
   * decidiu.
   */
  Object.freeze({
    id: 'skin_silver_hunter',
    displayName: 'Silver Hunter',
    blurb:
      'Casco prateado de linhas limpas, com brilho frio nas bordas. ' +
      'É puramente cosmético: não altera dano, vida, velocidade, cadência nem nada da partida.',
    assetKey: 'skin-silver-hunter',
    enabled: true,
  }),
  Object.freeze({
    id: 'skin_golden_wing',
    displayName: 'Golden Wing',
    blurb:
      'Casco dourado de asas largas, com faíscas quentes acompanhando o voo. ' +
      'É puramente cosmético: não altera dano, vida, velocidade, cadência nem nada da partida.',
    assetKey: 'skin-golden-wing',
    enabled: true,
  }),
  Object.freeze({
    id: 'skin_cyber_rainbow',
    displayName: 'Cyber Rainbow',
    blurb:
      'Casco iridescente em ciano, magenta e violeta, com rastro prismático. ' +
      'É puramente cosmético: não altera dano, vida, velocidade, cadência nem nada da partida.',
    assetKey: 'skin-cyber-rainbow',
    enabled: true,
  }),
  Object.freeze({
    id: FOUNDATION_SKIN_ID,
    displayName: 'Pulso Violeta da Fundação',
    blurb:
      'Uma borda de energia violeta que acompanha o contorno da sua nave. ' +
      'É puramente cosmética: não altera dano, vida, velocidade, cadência nem nada da partida.',
    /*
     * Disabled: there is no PNG, no `assetKey`, no VFX descriptor and no Cash
     * banner. It is content that is not finished, not content that was taken
     * away, and it is unobtainable until all four exist (ADR 0039).
     */
    assetKey: null,
    enabled: false,
  }),
]);

export function findSkinDefinition(id: string): SkinDefinitionEntry | null {
  return SKIN_DEFINITIONS.find((entry) => entry.id === id) ?? null;
}

/**
 * Every skin a Cash banner could legitimately offer today.
 *
 * The three of the Closed Alpha collection. A banner may only carry a
 * definition this function returns, and `enabled` is what puts one here - the
 * same flag `grantInstanceCore` refuses on, so "the banner may offer it" and
 * "the vault may create it" cannot drift apart.
 *
 * It replaces `redeemableSkins()`, which answered a question - "what may be
 * claimed for free" - the product no longer asks.
 */
export function obtainableSkins(): readonly SkinDefinitionEntry[] {
  return SKIN_DEFINITIONS.filter((entry) => entry.enabled);
}
